import JSZip from 'jszip';

export const OFFICE_EXTENSIONS = ['.docx', '.xlsx', '.xlsm', '.pptx'];

export const emptyOfficeMetadata = () => ({
  creator: '',
  lastModifiedBy: '',
  created: '',
  modified: '',
  revision: '',
  totalTime: '',
  company: '',
  application: '',
});

const parseXMLString = (xmlString) => {
  const doc = new DOMParser().parseFromString(xmlString, 'text/xml');
  if (doc.querySelector('parsererror')) {
    throw new Error('The Office metadata XML is invalid.');
  }
  return doc;
};

const serializeXML = (doc) => new XMLSerializer().serializeToString(doc);

const toInputDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 16);
};

const toOfficeDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : `${date.toISOString().slice(0, 19)}Z`;
};

const findElement = (doc, localName) =>
  Array.from(doc.getElementsByTagName('*')).find((element) => element.localName === localName);

const setExistingElement = (doc, localName, value) => {
  const element = findElement(doc, localName);
  if (element && value !== '') element.textContent = value;
};

const setCoreElement = (doc, localName, value) => {
  let element = findElement(doc, localName);
  if (!element && value !== '') {
    element = doc.createElementNS(
      'http://schemas.openxmlformats.org/package/2006/metadata/core-properties',
      `cp:${localName}`
    );
    doc.documentElement.appendChild(element);
  }
  if (element && value !== '') element.textContent = value;
};

export async function readOfficeMetadata(file) {
  const zip = await JSZip.loadAsync(file);
  const coreXML = await zip.file('docProps/core.xml')?.async('text');
  const appXML = await zip.file('docProps/app.xml')?.async('text');
  const metadata = emptyOfficeMetadata();

  if (coreXML) {
    const doc = parseXMLString(coreXML);
    metadata.creator = findElement(doc, 'creator')?.textContent || '';
    metadata.lastModifiedBy = findElement(doc, 'lastModifiedBy')?.textContent || '';
    metadata.created = toInputDate(findElement(doc, 'created')?.textContent);
    metadata.modified = toInputDate(findElement(doc, 'modified')?.textContent);
    metadata.revision = findElement(doc, 'revision')?.textContent || '';
  }

  if (appXML) {
    const doc = parseXMLString(appXML);
    metadata.totalTime = findElement(doc, 'TotalTime')?.textContent || '';
    metadata.company = findElement(doc, 'Company')?.textContent || '';
    metadata.application = findElement(doc, 'Application')?.textContent || '';
  }

  return { metadata, source: { kind: 'office', zip } };
}

export async function writeOfficeMetadata(source, metadata) {
  const zip = source.zip.clone();
  const coreXML = await zip.file('docProps/core.xml')?.async('text');

  if (coreXML) {
    const doc = parseXMLString(coreXML);
    setExistingElement(doc, 'creator', metadata.creator);
    setCoreElement(doc, 'lastModifiedBy', metadata.lastModifiedBy);
    setExistingElement(doc, 'created', toOfficeDate(metadata.created));
    setExistingElement(doc, 'modified', toOfficeDate(metadata.modified));
    setCoreElement(doc, 'revision', metadata.revision);
    zip.file('docProps/core.xml', serializeXML(doc));
  }

  const appXML = await zip.file('docProps/app.xml')?.async('text');
  if (appXML) {
    const doc = parseXMLString(appXML);
    setExistingElement(doc, 'TotalTime', metadata.totalTime);
    setExistingElement(doc, 'Company', metadata.company);
    setExistingElement(doc, 'Application', metadata.application);
    zip.file('docProps/app.xml', serializeXML(doc));
  }

  return zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}
